import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@compiled/react';
import get from 'lodash/get';
import { v4 as uuid } from 'uuid';
import { allFeatureFlagsAccessed } from '@atlaskit/react-ufo/feature-flags-accessed';
import { token } from '@atlaskit/tokens';
import { JiraIssueCreateErrorBoundary } from '@atlassian/jira-issue-create-analytics-common/src/ui/jira-issue-create-error-boundary/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isForgeCustomField } from '@atlassian/jira-forge-field-base/src/utils/index.tsx';
import { getErrorType } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-error-type/index.tsx';
import { UiModificationsErrorHandler } from '@atlassian/ui-modifications-core';
import {
	globalIssueCreateModalLoad,
	globalIssueCreateModalLoadSubsequent,
} from '@atlassian/jira-issue-create-analytics/src/services/performance-analytics/index.tsx';
import type { FormFieldValues } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import {
	fireTrackAnalyticsDeferred,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import {
	globalIssueCreateTTIWithAdjustments,
	markGicTti as issueAdjustmentsMarkGicTti,
} from '@atlassian/jira-ui-modifications-analytics/src/common/utils/performance-analytics/gic.tsx';
import { GIC_VIEW_TYPE } from '@atlassian/jira-ui-modifications-view-gic/src/common/constants/index.tsx';
import { FieldDecoratorAsync } from '@atlassian/jira-ui-modifications-view-gic/src/ui/field-decorator/index.tsx';
import { getGqlFieldMap } from '../../../../../common/constants/field-map.tsx';
import {
	EPIC_LINK_FIELD,
	PARENT_LINK_FIELD,
} from '../../../../../common/constants/graphql-field-types.tsx';
import IncompatibleField from '../../../../../common/ui/fields/incompatible-field/index.tsx';
import { preventEscKeyPropagationKeyPressHandler } from '../../../../../common/utils/index.tsx';
import { useIsGICPositioningEnabled } from '../../../../../controllers/gic-positioning-enabled/main.tsx';
import {
	FIELDS_NEEDING_COMPLETE_FORM_STATE,
	FIELDS_NEEDING_CREATE_LINKED_ISSUE_INFO,
	FIELDS_NEEDING_FORM_CONTEXT,
	FIELDS_NEEDING_GET_FORM_STATE,
	FIELDS_WITHOUT_DROPDOWN_MENU,
	FIELDS_WITH_EXTENSION_PROPS,
	FIELDS_WITH_ON_INPROGRESS_CHANGE,
	FIELDS_WITH_SELECTED_ISSUE_TYPE,
	FIELDS_WITH_SELECTED_PROJECT,
	FORCE_RENDER_ON_TAB_CHANGE,
} from './constants.tsx';
import type { ExtraProps, FieldContainerWrapperProps, Props } from './types.tsx';
import { getFieldsToRenderInOneBatch, getHelpTextUrl, getOnLinkClickEvent } from './utils.tsx';

/**
 * This hook memoizes getFormState function when it has a valid value
 * If we don't do this, the fields to which we pass getFormState will rerender everytime any other field changes (even for every keyboard stroke)
 */
const useMemoizedGetFormState = (getFormState?: () => FormFieldValues) => {
	const ref = useRef(getFormState); // Initialise with getFormState

	useMemo(() => {
		// If there's a valid value already, we keep using it. We can assume that getFormState doesn't get stale since it is a function which returns state
		if (ref.current) {
			return;
		}
		// Use new value if previous ref.current is not valid
		ref.current = getFormState;
	}, [getFormState]);

	return ref.current;
};

export const FieldContainerWrapper = (props: FieldContainerWrapperProps) => {
	const {
		onFieldMount,
		onKeyDown,
		fieldId,
		componentProps,
		Component,
		apiLatency,
		isSubsequentLoad,
		issueAdjustmentModules,
		wasStateRestored,
	} = props;

	const [, { setIsFormLoaded }] = useFormContext();

	const isMountEventFired = useRef(false);

	useEffect(() => {
		if (isMountEventFired.current === false) {
			isMountEventFired.current = true;
			if ('requestIdleCallback' in window) {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.requestIdleCallback(() => onFieldMount(fieldId), {
					timeout: 50,
				});
			} else {
				setTimeout(() => {
					onFieldMount(fieldId);
				}, 50);
			}
		}
	}, [onFieldMount, fieldId]);

	const onLayoutDone = useCallback(() => {
		const globalIssueCreateModalLoadMetric = isSubsequentLoad
			? globalIssueCreateModalLoadSubsequent
			: globalIssueCreateModalLoad;

		// @ts-expect-error - TS2445 - Property 'state' is protected and only accessible within class 'BaseMetric' and its subclasses.
		if (globalIssueCreateModalLoadMetric.state === 'started') {
			const stopTime = Math.floor(performance.now());
			const customData = {
				apiLatency,
				featureFlags: Object.fromEntries(allFeatureFlagsAccessed),
				// TODO: Need to understand & update logic code to fix this issue, e.g. checking null and return a default value
				// @ts-expect-error - TS2531 - Object is possibly 'null'. | TS2445 - Property 'startTime' is protected and only accessible within class 'BaseMetric' and its subclasses.
				tti: Math.floor(stopTime - globalIssueCreateModalLoadMetric.startTime),
				isFirstTime: !isSubsequentLoad,
				wasStateRestored,
			};

			globalIssueCreateModalLoadMetric.stop({
				// @ts-expect-error - TS2322 - Type '{ apiLatency: number | null; tti: number; isFirstTime: boolean; }' is not assignable to type 'CustomValues'.
				customData,
			});
			issueAdjustmentsMarkGicTti();
		}
		setIsFormLoaded(true);
	}, [apiLatency, isSubsequentLoad, setIsFormLoaded, wasStateRestored]);

	const extendedComponentProps = { ...componentProps, onLayoutDone };

	return (
		<FieldContainer
			onKeyDown={onKeyDown}
			key={fieldId}
			id={`${fieldId}-container`}
			data-testid={`issue-create.ui.modal.create-form.layout-renderer.field-renderer.field.${fieldId}`}
		>
			{issueAdjustmentModules.length > 0 ? (
				<JiraIssueCreateErrorBoundary
					id="globalIssueCreateIssueAdjustmentsAdapter"
					packageName="issueCreateModal"
					fallback={() => (
						<>
							<UiModificationsErrorHandler />
							<Component {...extendedComponentProps} />
						</>
					)}
					sendToPrivacyUnsafeSplunk
					attributes={(error: Error) => ({
						errorType: getErrorType(error),
						viewType: GIC_VIEW_TYPE,
						fieldType: extendedComponentProps.fieldType,
						fieldId,
					})}
				>
					<FieldDecoratorAsync Component={Component} componentProps={extendedComponentProps} />
				</JiraIssueCreateErrorBoundary>
			) : (
				<Component {...extendedComponentProps} />
			)}
		</FieldContainer>
	);
};

const FieldRendererBase = (props: Props) => {
	const isGICPositioningEnabled = useIsGICPositioningEnabled();
	const [
		{
			isSubTaskCreationOpen,
			isCreateLinkedIssue,
			isSubsequentLoad,
			minimizableModalView,
			wasStateRestored,
			isMiniDesignEnabled,
			FieldWrapperComponent,
			isModalOpen,
		},
	] = useTriggerIssueCreateModal();

	const { fieldConfig, getFormState, apiLatency, setFieldValue, isCollapsed, isFieldResponsive } =
		props;

	/**
	 * hiding "epic link" and "parent link fields by filtering them based on their type.
	 * Note that this is a temporary frontend solution until we remove these fields in the backend,
	 * which is scheduled to be done in FY25 Q3.
	 */
	const fields = props.fields.filter((fieldId) => {
		// @ts-expect-error - TS2339 - Property 'gqlType' does not exist on type '(BaseField & FieldConfigValueWithoutBaseField) | {}'.
		const { gqlType } = get(fieldConfig, fieldId, {});
		return gqlType !== EPIC_LINK_FIELD && gqlType !== PARENT_LINK_FIELD;
	});

	// TODO Add this field with the fields on previous line when cleaning FF
	const fieldPropsExtensions = isGICPositioningEnabled() ? props.fieldPropsExtensions : undefined;
	const [
		{
			selectedProject,
			selectedIssueType,
			shouldAutoFocus,
			ecosystem: {
				forge: { customFields, filteredOutModules, issueAdjustmentModules, hasFinishedLoading },
			},
			currentActiveTabIndex,
		},
		{ setIsOperationInProgress },
	] = useFormContext();
	const renderAllFieldsInTabStartTime = useRef<number>(0);
	// Start by rendering the first field alone to give the user some visual feedback quickly
	// should be increased to a full "batch" of ~10 fields when field rendering performance is improved
	const [fieldsToRenderCount, setFieldsToRenderCount] = useState<number>(1);

	const fieldsMountedCount = useRef(0);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isMini = minimizableModalView === 'mini' || isMiniDesignEnabled;

	const renderedAllFields = useRef(false);

	const getFormStateMemoized = useMemoizedGetFormState(getFormState);

	useEffect(() => {
		renderAllFieldsInTabStartTime.current = performance.now();
		setIsOperationInProgress(true);
		const analyticsEvent = createAnalyticsEvent({});
		fireTrackAnalyticsDeferred(analyticsEvent, 'gic renderAllFieldsInTabStart', {});
		renderedAllFields.current = false;
	}, [setIsOperationInProgress, createAnalyticsEvent]);

	const fieldsLength = fields.length;

	const fieldsRendererSessionId = useRef('');

	const fieldsAlreadyFullyLoaded = useRef(false);

	useEffect(() => {
		if (!isModalOpen) return;

		fieldsRendererSessionId.current = uuid();
		fieldsAlreadyFullyLoaded.current = false;
		const analyticsEvent = createAnalyticsEvent({});
		fireTrackAnalyticsDeferred(analyticsEvent, 'gic fieldsToRenderTab', {
			fieldsLength,
			fieldsRendererSessionId: fieldsRendererSessionId.current,
		});
	}, [createAnalyticsEvent, fieldsLength, isModalOpen]);

	useEffect(() => {
		if (!isModalOpen || fieldsToRenderCount < fieldsLength || fieldsAlreadyFullyLoaded.current)
			return;

		fieldsAlreadyFullyLoaded.current = true;
		const analyticsEvent = createAnalyticsEvent({});
		fireTrackAnalyticsDeferred(analyticsEvent, 'gic fieldsFullyRenderedTab', {
			fieldsLength,
			fieldsRendererSessionId: fieldsRendererSessionId.current,
		});
	}, [createAnalyticsEvent, fieldsLength, fieldsToRenderCount, isModalOpen]);

	const onFieldMount = () => {
		fieldsMountedCount.current += 1;
		if (fieldsMountedCount.current >= fieldsToRenderCount) {
			setFieldsToRenderCount(
				(currentCount) => currentCount + getFieldsToRenderInOneBatch(fields.length),
			);
		}

		if (fieldsMountedCount.current >= fields.length && !renderedAllFields.current) {
			setIsOperationInProgress(false);
			// send event to monitor in different browsers that the fields rendered successfully
			const analyticsEvent = createAnalyticsEvent({});
			const attributes = {
				timeToRenderAllFieldsInTabMs: Math.floor(
					performance.now() - renderAllFieldsInTabStartTime.current,
				),
				fields,
			};
			fireTrackAnalyticsDeferred(analyticsEvent, 'gic renderedAllFieldsInTab', attributes);
			renderedAllFields.current = true;
		}

		// The last call to `mark` is the time used in the final metric
		// We use this fact to obtain when the final field was mounted
		globalIssueCreateTTIWithAdjustments.mark('gic-all-fields-loaded');
	};

	const fieldsToRender = fields.slice(0, fieldsToRenderCount).map((fieldId, index) => {
		let content = null;
		// @ts-expect-error - TS2339 - Property 'gqlType' does not exist on type '(BaseField & FieldConfigValueWithoutBaseField) | {}'.
		const { gqlType, ...rest } = get(fieldConfig, fieldId, {});
		// @ts-expect-error - TS2339 - Property 'fieldName' does not exist on type '{ fieldId: string; fieldName: string; description?: string | undefined; isRequired?: boolean | undefined; isEditable?: boolean | undefined; nonEditableReason?: { message?: string | undefined; } | undefined; ... 6 more ...; onLayoutDone?: (() => void) | undefined; } | {}'. | TS2339 - Property 'description' does not exist on type '{ fieldId: string; fieldName: string; description?: string | undefined; isRequired?: boolean | undefined; isEditable?: boolean | undefined; nonEditableReason?: { message?: string | undefined; } | undefined; ... 6 more ...; onLayoutDone?: (() => void) | undefined; } | {}'. | TS2339 - Property 'isRequired' does not exist on type '{ fieldId: string; fieldName: string; description?: string | undefined; isRequired?: boolean | undefined; isEditable?: boolean | undefined; nonEditableReason?: { message?: string | undefined; } | undefined; ... 6 more ...; onLayoutDone?: (() => void) | undefined; } | {}'. | TS2339 - Property 'fieldType' does not exist on type '{ fieldId: string; fieldName: string; description?: string | undefined; isRequired?: boolean | undefined; isEditable?: boolean | undefined; nonEditableReason?: { message?: string | undefined; } | undefined; ... 6 more ...; onLayoutDone?: (() => void) | undefined; } | {}'.
		const { fieldName, description, isRequired, fieldType } = rest;
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly JiraAffectedServicesField: { readonly Component: AbstractComponent<ReturnComponentProps<AffectedServicesFieldConfig>, any>; readonly defaultValueKey: "selectedAffectedServices"; readonly submitTransform: (options: AffectedServiceOption[]) => ServiceEntity[]; readonly retainUserInputTransform: (selectedAff...'.
		const FieldDetails = getGqlFieldMap()[gqlType];
		const commonAttributesInfo: Pick<
			ExtraProps,
			| 'autoFocus'
			| 'isSubTaskCreationOpen'
			| 'isDropdownMenuFixedAndLayered'
			| 'componentKey'
			| 'isMini'
			| 'isCollapsed'
			| 'isFieldResponsive'
			| 'isCreateLinkedIssue'
		> = {
			autoFocus:
				index === 0 &&
				currentActiveTabIndex === 0 &&
				FIELDS_WITHOUT_DROPDOWN_MENU.includes(gqlType) &&
				shouldAutoFocus,
			isSubTaskCreationOpen,
			isDropdownMenuFixedAndLayered: true,
			componentKey: FORCE_RENDER_ON_TAB_CHANGE.includes(gqlType)
				? `${fieldId}-${currentActiveTabIndex}`
				: undefined,
			isCreateLinkedIssue:
				FIELDS_NEEDING_CREATE_LINKED_ISSUE_INFO.includes(gqlType) &&
				fg('create_linked_issues_trigger_point_issue_view')
					? isCreateLinkedIssue
					: false,
			isMini,
			...(fg('migrate_gic_smart_fields_jira_labs') && {
				isCollapsed,
				isFieldResponsive,
			}),
		};
		const extraProps: ExtraProps = {
			...commonAttributesInfo,
			selectedProject: FIELDS_WITH_SELECTED_PROJECT.includes(gqlType) ? selectedProject : undefined,
			selectedIssueType: FIELDS_WITH_SELECTED_ISSUE_TYPE.includes(gqlType)
				? selectedIssueType
				: undefined,
			// @ts-expect-error - TS2322 - Type 'HookFunction<State, BoundActions<State, { readonly setCurrentActiveTabIndex: (tabIndex: number) => Action<State, void, void | Promise<void>>; readonly setSelectedProject: (project: Project) => Action<...>; ... 20 more ...; readonly setSummaryValue: (summary: string) => Action<...>; }>, void> | undefined' is not assignable to type 'HookFunction<State, { readonly setCurrentActiveTabIndex: (tabIndex: number) => Action<State, void, void | Promise<void>>; readonly setSelectedProject: (project: Project) => Action<...>; ... 20 more ...; readonly setSummaryValue: (summary: string) => Action<...>; }, undefined> | undefined'.
			useFormContext: FIELDS_NEEDING_FORM_CONTEXT.includes(gqlType) ? useFormContext : undefined,
			contextAndValuesOfIssueBeingCreated: FIELDS_NEEDING_COMPLETE_FORM_STATE.includes(gqlType)
				? { getFormState, selectedIssueType, fieldConfig }
				: undefined,
			getFormState: FIELDS_NEEDING_GET_FORM_STATE.includes(gqlType)
				? getFormStateMemoized
				: undefined,
			onInProgressChange: FIELDS_WITH_ON_INPROGRESS_CHANGE.includes(gqlType)
				? setIsOperationInProgress
				: undefined,
			forge:
				fieldType !== undefined && isForgeCustomField(fieldType)
					? {
							selectedProject,
							selectedIssueType,
							modules: customFields,
							filteredOutModules,
							hasFinishedLoading,
						}
					: undefined,
			fieldHelpTextUrl: getHelpTextUrl(gqlType),
			onLinkClickEvent: getOnLinkClickEvent(gqlType),
		};

		if (
			isGICPositioningEnabled() &&
			fieldPropsExtensions?.[fieldType] &&
			FIELDS_WITH_EXTENSION_PROPS.includes(gqlType)
		) {
			Object.assign(extraProps, fieldPropsExtensions[fieldType]);
		}

		if (FieldDetails) {
			const { Component, defaultValueKey } = FieldDetails;
			const fieldContainerWrapperProps = {
				...(defaultValueKey === 'parentIssue'
					? {}
					: { onKeyDown: preventEscKeyPropagationKeyPressHandler }),
				fieldId,
				componentProps: {
					...rest,
					...extraProps,
					fieldName,
					description,
					gqlType,
					fieldWrapperElement: FieldWrapperComponent ? (
						<FieldWrapperComponent
							fieldId={fieldId}
							field={get(fieldConfig, fieldId)}
							setFieldValue={setFieldValue}
						/>
					) : null,
				},
				Component,
				onFieldMount,
				apiLatency,
				isSubsequentLoad,
				issueAdjustmentModules,
				setFieldValue,
				wasStateRestored,
			};
			// @ts-expect-error - TS2322 - Type '{ onKeyDown: (e: KeyboardEvent<Element> | KeyboardEvent<EventTarget>) => void; fieldId: string; componentProps: { fieldName: any; ... 24 more ...; onLayoutDone?: (() => void) | undefined; }; ... 7 more ...; key: string; }' is not assignable to type 'FieldContainerWrapperProps'.
			content = <FieldContainerWrapper key={fieldId} {...fieldContainerWrapperProps} />;
		} else {
			content = (
				<FieldContainer
					key={fieldId}
					data-testid={`issue-create.ui.modal.create-form.layout-renderer.field-renderer.field.${fieldId}`}
				>
					<IncompatibleField
						fieldId={fieldId}
						fieldName={fieldName}
						isRequired={isRequired}
						description={description}
						projectType={selectedProject?.projectType}
						gqlType={gqlType}
						fieldType={fieldType}
						onFieldMount={onFieldMount}
						isMini={isMini}
					/>
				</FieldContainer>
			);
		}
		const errorAttributes = {
			...commonAttributesInfo,
			isProjectSelected: FIELDS_WITH_SELECTED_PROJECT.includes(gqlType),
			selectedIssueType: FIELDS_WITH_SELECTED_ISSUE_TYPE.includes(gqlType),
			isFormContextProvided: FIELDS_NEEDING_FORM_CONTEXT.includes(gqlType),
			IscontextAndValuesOfIssueBeingCreatedProvided:
				!!FIELDS_NEEDING_COMPLETE_FORM_STATE.includes(gqlType),
			isGetFormStateProvided: FIELDS_NEEDING_GET_FORM_STATE.includes(gqlType),
			isOnInProgressChangeProvided: FIELDS_WITH_ON_INPROGRESS_CHANGE.includes(gqlType),
			isForgeField: fieldType !== undefined && isForgeCustomField(fieldType),
			isFieldHelpTextUrlProvided: !!getHelpTextUrl(gqlType),
			isOnLinkClickEventProvided: !!getOnLinkClickEvent(gqlType),
			gqlType,
			fieldType,
		};

		if (fg('refactor-gic-field-error-boundary')) {
			return (
				<JiraIssueCreateErrorBoundary
					id="globalIssueCreateIssue-field-renderer"
					key={`field-renderer-${fieldId}`}
					packageName="jiraIssueCreateModal"
					fallback="unmount"
					onError={() => {
						onFieldMount();
					}}
					attributes={errorAttributes}
					sendToPrivacyUnsafeSplunk
				>
					{content}
				</JiraIssueCreateErrorBoundary>
			);
		}
		return (
			<JiraIssueCreateErrorBoundary
				id={`globalIssueCreateIssue-field-renderer-${fieldId}`}
				key={`field-renderer-${fieldId}`}
				packageName="jiraIssueCreateModal"
				fallback="unmount"
				onError={() => {
					onFieldMount();
				}}
				sendToPrivacyUnsafeSplunk
			>
				{content}
			</JiraIssueCreateErrorBoundary>
		);
	});
	return fieldsToRender;
};

const FieldRenderer = memo<Props>(FieldRendererBase);
export default FieldRenderer;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldContainer = styled.div({
	marginTop: token('space.250'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& input[disabled]': {
		backgroundColor: token('color.background.disabled'),
	},
});
